import { CalendarOutlined, CheckOutlined, ClockCircleOutlined, EditOutlined } from "@ant-design/icons";
import { Button, Flex, Input, InputRef, Typography, message } from "antd";
import dayjs from "dayjs";
import pdfMake from "pdfmake/build/pdfmake";
import pdfFonts from "pdfmake/build/vfs_fonts";
import "pdfmake/build/vfs_fonts.js";
import React from "react";
import { CATEGORY, GENDER_TYPE } from "../../domain/notes.domain";
import { useAppDispatch, useAppSelector } from "../../hooks/redux-hooks";
import { updateNote } from "../../service/notes.service";
import { setShowDemoSignupModal } from "../../slices/appStateSlice";
import { setNotesDetails } from "../../slices/notesSlice";
import { durationTimeFromType } from "../../utils/datamanipulation.utils";
import styles from "./notes.module.scss";

// @ts-ignore pdfmake types missing vfs property
(pdfMake as any).vfs = pdfFonts.vfs;

type TProps = {
  notesDetails: any;
};

export const CUSTOMISE_ICON = (
  <svg width="14" height="14" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path
      d="M2.33333 1V3.66667M1 2.33333H3.66667M3 10.3333V13M1.66667 11.6667H4.33333M7.66667 1L9.19048 5.57143L13 7L9.19048 8.42857L7.66667 13L6.14286 8.42857L2.33333 7L6.14286 5.57143L7.66667 1Z"
      stroke="#6366F1"
      strokeWidth="1.33333"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const NotesHeader = ({ notesDetails }: TProps) => {
  const dispatch = useAppDispatch();
  const componentName = "notes";
  const titleRef = React.useRef<InputRef>(null);

  const { isDemoPath } = useAppSelector((state) => state.appState);
  const [editTitle, setEditTitle] = React.useState(false);

  const updateTitle = async () => {
    setEditTitle(false);
    const value = titleRef.current?.input?.value;
    if (value && notesDetails && value !== notesDetails?.title) {
      const updateNoteData = {
        audio_id: notesDetails.id,
        title: value,
      };
      await updateNote(updateNoteData)
        .then(() => {
          message.success("Title updated successfully");
          dispatch(
            setNotesDetails({
              ...notesDetails,
              title: value,
            }),
          );
        })
        .catch((error) => {
          console.error(error);
        });
    }
  };

  const categoryLabel = (type: number | undefined) => {
    switch (type) {
      case CATEGORY.INDIVIDUAL:
        return "Individual";
      case CATEGORY.RELATIONSHIP:
        return "Relationship";
      case CATEGORY.FAMILY:
        return "Family";
      case CATEGORY.GROUP:
        return "Group";
      default:
        return "";
    }
  };

  return (
    <>
      {/* Row 1: Date + Title (with edit) */}
      <Flex
        gap={6}
        align="center"
        className={`${styles[`${componentName}__main-row-meta`]}`}
        style={{ marginBottom: 8 }}
      >
        <CalendarOutlined style={{ color: "#52565F", fontSize: 13 }} />
        <span style={{ color: "#52565F", fontSize: 14, fontWeight: 500, whiteSpace: "nowrap" }}>
          {dayjs(notesDetails?.uploaded_at).format("MMM D, YYYY")} at{" "}
          {dayjs(notesDetails?.uploaded_at).format("h:mm A")}
        </span>
        <span className={`${styles[`${componentName}__main-row-meta-dot`]}`}>&middot;</span>
        <span className={`${styles[`${componentName}__main-row-meta-title`]}`}>
          {editTitle ? (
            <Input
              defaultValue={notesDetails?.title || "Untitled Session"}
              ref={titleRef}
              onBlur={updateTitle}
              size="small"
              style={{ fontSize: 14, color: "#52565F", maxWidth: 300 }}
            />
          ) : (
            <span title={notesDetails?.title || "Untitled Session"}>{notesDetails?.title || "Untitled Session"}</span>
          )}
          &nbsp;
          {!editTitle ? (
            <Button
              shape="circle"
              size="small"
              icon={<EditOutlined style={{ fontSize: 12 }} />}
              className={`${styles[`${componentName}__main-section-header-edit`]}`}
              onClick={() => {
                if (isDemoPath) {
                  dispatch(setShowDemoSignupModal(true));
                  return;
                }
                setEditTitle(true);
              }}
            />
          ) : (
            <Button
              shape="circle"
              type="primary"
              size="small"
              className={`${styles[`${componentName}__main-section-header-save`]}`}
              icon={<CheckOutlined style={{ fontSize: 12 }} />}
              onClick={updateTitle}
            />
          )}
        </span>
      </Flex>
      {/* Row 2: Patient Name + Gender */}
      {notesDetails && notesDetails.client_name && (
        <Flex align="center" gap={8} style={{ marginBottom: 8 }}>
          <Typography.Text strong style={{ fontSize: 16, color: "#111827", fontWeight: 600, lineHeight: "140%" }}>
            {notesDetails.client_name}
          </Typography.Text>
          {notesDetails?.gender_type === GENDER_TYPE.MALE && (
            <span style={{ color: "#6b7280", fontSize: 14 }}>(M)</span>
          )}
          {notesDetails?.gender_type === GENDER_TYPE.FEMALE && (
            <span style={{ color: "#6b7280", fontSize: 14 }}>(F)</span>
          )}
        </Flex>
      )}
      {/* Row 3: Pill Badges */}
      <Flex wrap="wrap" gap={6} className={`${styles[`${componentName}__main-row-pills`]}`}>
        {notesDetails?.note_type_info?.name && (
          <span className={`${styles["pill-note-type"]}`}>Note Type: {notesDetails.note_type_info.name}</span>
        )}
        {categoryLabel(notesDetails?.category_type) && (
          <span className={`${styles["pill-session-type"]}`}>{categoryLabel(notesDetails?.category_type)}</span>
        )}
        {notesDetails?.duration_type !== null && notesDetails?.duration_type !== undefined && (
          <span className={`${styles["pill-duration"]}`}>
            <ClockCircleOutlined style={{ marginRight: 4, fontSize: 10 }} />
            {durationTimeFromType(notesDetails.duration_type)}
          </span>
        )}
        {notesDetails?.client_dob && <span className={`${styles["pill-info"]}`}>DOB: {notesDetails.client_dob}</span>}
        {notesDetails?.client_mrn && <span className={`${styles["pill-info"]}`}>MRN: {notesDetails.client_mrn}</span>}
      </Flex>
      {!notesDetails.is_owned && notesDetails?.owner_name && (
        <Flex style={{ marginTop: "10px", color: "var(--grey-text)" }}>
          Note created by: {notesDetails?.owner_name}
        </Flex>
      )}
    </>
  );
};

export default NotesHeader;
